import React, { useState, useEffect } from 'react';
import ApplyPackageDialog from '../../packages/ApplyPackageDialog';
import AdjustSubscriptionDialog from '../../packages/subscription/AdjustSubscriptionDialog';
import { adjustCompanySubscription } from '../../packages/companyPackageApi';
import { adminGetCompanyByOwnerId } from '../../../api/packagesApi';
import {
  getCompanySubscription,
  getGracePeriodDays,
  resolveSubscriptionStatus,
  toSubscriptionDate,
} from '../../../../packages/utils/subscriptionStatus';
import { computeBillingPrice } from '../../../../packages/utils/packagePricing';
import { getResourceCounts, normalizePackageFeatures } from '../../../../packages/utils/packageFeatures';
import './styles/UserPackage.css';

const CompensationDialog = ({ open, onClose, onSuccess, companyId }) => {
  const [days, setDays] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!companyId) {
      setError('Company information not available');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await adjustCompanySubscription({
        companyId,
        daysToAdd: parseInt(days, 10),
        reason,
      });
      setDays('');
      setReason('');
      // Force an immediate refresh of company details
      await onSuccess();
      onClose();
    } catch (err) {
      console.error('Error adding compensation time:', err);
      setError(err.message || 'Failed to add compensation time');
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    if (loading) return;
    onClose?.();
  };

  if (!open) return null;

  return (
    <div
      className="upModalOverlay"
      role="dialog"
      aria-modal="true"
      aria-label="Add compensation time"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="upModal">
        <h4 className="upModalTitle">Add compensation time</h4>
        <p className="upModalBody">Grant extra days and record the reason for auditability.</p>

        {error ? <div className="upAlert upAlertError">{error}</div> : null}

        <form onSubmit={handleSubmit}>
          <div className="upField">
            <label htmlFor="compDays">Days</label>
            <input
              id="compDays"
              className="upInput"
              type="number"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              required
              disabled={loading}
              min={1}
            />
          </div>
          <div className="upField">
            <label htmlFor="compReason">Reason</label>
            <textarea
              id="compReason"
              className="upTextarea"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              disabled={loading}
              rows={4}
            />
          </div>

          <div className="upModalActions">
            <button type="button" className="upBtn" onClick={close} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="upBtn upBtnPrimary" disabled={loading}>
              {loading ? (
                <>
                  <span className="upSpinner" aria-hidden="true" />
                  Adding…
                </>
              ) : (
                'Add'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CompensationHistory = ({ history, loading, error }) => {
  if (loading) {
    return (
      <div className="upHistoryEmpty">Loading…</div>
    );
  }

  if (error) {
    return (
      <div className="upAlert upAlertError">{error}</div>
    );
  }

  if (!history?.length) {
    return (
      <div className="upHistoryEmpty">No compensation history available.</div>
    );
  }

  return (
    <div className="upList">
      {history.map((item, index) => (
        <div className="upListItem" key={item.id || index}>
          <div>
            <div className="upListItemTitle">{item.days} days added</div>
            <div className="upListItemSub">Reason: {item.reason}</div>
            <div className="upListItemSub">{new Date(item.timestamp?.toDate?.() || item.timestamp).toLocaleString()}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

const UserPackage = ({ user, setUser }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openAdjustDialog, setOpenAdjustDialog] = useState(false);
  const [company, setCompany] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [openCompensationDialog, setOpenCompensationDialog] = useState(false);
  const [showCompensationHistory, setShowCompensationHistory] = useState(false);
  const [compensationHistory, setCompensationHistory] = useState([]);
  const [loadingCompensation, setLoadingCompensation] = useState(false);
  const [compensationError, setCompensationError] = useState(null);

  const fetchCompanyDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const companyData = await adminGetCompanyByOwnerId(user.id);
      if (companyData) {
        setCompany(companyData);

        const rawSub = getCompanySubscription(companyData);
        const resolved = resolveSubscriptionStatus(rawSub, getGracePeriodDays(companyData));
        setSubscription(rawSub ? { ...rawSub, ...resolved } : null);

        const history = rawSub?.compensationHistory || [];
        setCompensationHistory(
          history.map((record, index) => {
            const addedAt = toSubscriptionDate(record.addedAt) || new Date();
            return {
              id: addedAt.getTime() || index,
              days: record.daysAdded,
              reason: record.reason,
              timestamp: addedAt,
            };
          })
        );
      } else {
        console.log('No company found for user:', user.id);
        setError('No company found for this user');
      }
    } catch (err) {
      console.error('Error fetching company details:', err);
      setError('Failed to load company details');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompensationHistory = async () => {
    if (!company) return;

    setLoadingCompensation(true);
    setCompensationError(null);

    try {
      const history = getCompanySubscription(company)?.compensationHistory || [];
      setCompensationHistory(
        history.map((record, index) => {
          const addedAt = toSubscriptionDate(record.addedAt) || new Date();
          return {
            id: addedAt.getTime() || index,
            days: record.daysAdded,
            reason: record.reason,
            timestamp: addedAt,
          };
        })
      );
    } catch (error) {
      console.error('Error fetching compensation history:', error);
      setCompensationError('Failed to load compensation history. Please try again.');
    } finally {
      setLoadingCompensation(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchCompanyDetails();
    } else {
      setLoading(false);
      setError('User information not available');
    }
  }, [user?.id]);

  const handleCompensationSuccess = async () => {
    try {
      await fetchCompanyDetails();
      await fetchCompensationHistory();
    } catch (error) {
      console.error('Error refreshing after compensation:', error);
    }
  };

  const getSubscriptionPill = () => {
    if (!subscription) return { label: 'Unknown', className: 'upPill upPillGray' };
    if (subscription.isPermanent) return { label: 'Permanent access', className: 'upPill upPillGreen' };
    const status = String(subscription.status || 'unknown').toLowerCase();
    if (status === 'active') return { label: 'Active', className: 'upPill upPillGreen' };
    if (status === 'expired') return { label: 'Expired', className: 'upPill upPillRed' };
    return { label: status, className: 'upPill upPillGray' };
  };

  const formatMoney = (n) => {
    const v = Number(n || 0);
    if (!Number.isFinite(v)) return '$0.00';
    return `$${v.toFixed(2)}`;
  };

  const formatDate = (d) => {
    if (!d) return 'N/A';
    if (typeof d?.toDate === 'function') return d.toDate().toLocaleDateString();
    if (typeof d?.seconds === 'number') return new Date(d.seconds * 1000).toLocaleDateString();
    const ms = new Date(d).getTime();
    return Number.isFinite(ms) ? new Date(ms).toLocaleDateString() : 'N/A';
  };

  if (!user?.id) {
    return (
      <div className="admin-card user-section-card userPackageCard">
        <div className="upAlert upAlertInfo">Please wait while user information is loading…</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-card user-section-card userPackageCard" aria-busy="true">
        <div className="upHeader">
          <div>
            <h3 className="upTitle">Package Details</h3>
            <p className="upSub">Loading company &amp; subscription…</p>
          </div>
        </div>
        <div className="upAlert upAlertInfo">Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-card user-section-card userPackageCard">
        <div className="upAlert upAlertError">{error}</div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="admin-card user-section-card userPackageCard">
        <div className="upAlert upAlertInfo">No company found for this user.</div>
      </div>
    );
  }

  const subPill = getSubscriptionPill();
  const resourceCounts = getResourceCounts(company);
  const featureList = normalizePackageFeatures(company?.package?.features);

  return (
    <div className="admin-card user-section-card userPackageCard">
      <div className="upHeader">
        <div>
          <h3 className="upTitle">Package Details</h3>
          <p className="upSub">{company.name}</p>
        </div>
        <div className="upBtnRow">
          <button type="button" className="upBtn" onClick={() => setOpenAdjustDialog(true)}>
            Adjust subscription
          </button>
          <button type="button" className="upBtn" onClick={() => setOpenCompensationDialog(true)}>
            Add time
          </button>
          <button type="button" className="upBtn upBtnPrimary" onClick={() => setOpenDialog(true)}>
            {company.package ? 'Change package' : 'Apply package'}
          </button>
        </div>
      </div>

      {/* Add compensation history toggle button */}
      <div className="upToggleRow">
        <button
          type="button"
          className="upBtn"
          onClick={() => {
            setShowCompensationHistory(!showCompensationHistory);
            if (!showCompensationHistory) {
              fetchCompensationHistory();
            }
          }}
        >
          {showCompensationHistory ? 'Hide' : 'Show'} compensation history
        </button>
      </div>

      {showCompensationHistory && (
        <div className="upHistoryCard">
          <div className="upHistoryHeader">
            <h4 className="upHistoryTitle">Compensation history</h4>
            <button type="button" className="upBtn" onClick={fetchCompensationHistory} disabled={loadingCompensation}>
              {loadingCompensation ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
          <CompensationHistory history={compensationHistory} loading={loadingCompensation} error={compensationError} />
        </div>
      )}

      {subscription && !subscription.isPermanent && subscription.status === 'expired' && (
        <div className="upAlert upAlertWarn">
          Your subscription has expired. Please renew to restore access to all features.
        </div>
      )}

      {company.package ? (
        <>
          <div className="upTopRow">
            <div>
              <div className="upSectionTitle">Subscription</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span className={subPill.className}>{subPill.label}</span>
                <span className="upPill upPillBlue">{company.package.name}</span>
                <span className="upPill upPillGray">
                  {(() => {
                    const cycle = company.package.subscription?.billingCycle || 'monthly';
                    const isAnnual = cycle === 'annually' || cycle === 'yearly';
                    let price = company.package.subscription?.currentPrice;
                    if (price == null || price === '') {
                      const monthlyPrice = parseFloat(company.package.price?.monthly || 0);
                      const annualDiscount = company.package.subscription?.annualDiscount ?? 0;
                      price = computeBillingPrice(monthlyPrice, cycle, annualDiscount);
                    }
                    return `${formatMoney(price)}/${isAnnual ? 'year' : 'month'}`;
                  })()}
                </span>
              </div>
            </div>
          </div>

          <div className="upKVs">
            <div className="upKV">
              <div className="upKVLabel">Next payment due</div>
              <div className="upKVValue">
                {subscription?.isPermanent ? 'Permanent access' : formatDate(subscription?.nextPaymentDue)}
              </div>
            </div>
            <div className="upKV">
              <div className="upKVLabel">Billing cycle</div>
              <div className="upKVValue">
                {subscription?.isPermanent
                  ? 'N/A'
                  : (subscription?.billingCycle === 'annually' || subscription?.billingCycle === 'yearly' ? 'Annual' : 'Monthly')}
              </div>
            </div>
            <div className="upKV">
              <div className="upKVLabel">Applied on</div>
              <div className="upKVValue">{company.package.appliedAt ? formatDate(company.package.appliedAt) : 'N/A'}</div>
            </div>
            <div className="upKV">
              <div className="upKVLabel">Company</div>
              <div className="upKVValue">{company.name}</div>
            </div>
          </div>

          <div className="upGrid2">
            <div>
              <h4 className="upSectionTitle">Resource usage</h4>
              <div className="upList">
                <div className="upListItem">
                  <div>
                    <div className="upListItemTitle">Employees</div>
                    <div className="upListItemSub">
                      {resourceCounts.employees} of {company.package?.limits?.employees === -1 ? 'Unlimited' : (company.package?.limits?.employees || 0)}
                    </div>
                  </div>
                </div>
                <div className="upListItem">
                  <div>
                    <div className="upListItemTitle">Appointments</div>
                    <div className="upListItemSub">
                      {resourceCounts.appointments}{' '}
                      {company.package?.limits?.appointments === -1 ? '(Unlimited)' : `of ${company.package?.limits?.appointments || 0}`}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="upSectionTitle">Features</h4>
              <div className="upList">
                {featureList.length > 0
                  ? featureList.map((feature) => {
                      const restricted = subscription?.status !== 'active'
                        && subscription?.status !== 'trial'
                        && subscription?.status !== 'grace_period'
                        && !subscription?.isPermanent;
                      return (
                        <div className="upListItem" key={feature}>
                          <div>
                            <div className="upListItemTitle">{feature}</div>
                            <div className="upListItemSub">
                              {restricted ? 'Enabled (restricted until subscription is active)' : 'Enabled'}
                            </div>
                          </div>
                          {restricted ? <span className="upPill upPillRed">Restricted</span> : null}
                        </div>
                      );
                    })
                  : <div className="upHistoryEmpty">No features data.</div>}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="upHistoryEmpty">No package assigned.</div>
      )}

      <CompensationDialog
        open={openCompensationDialog}
        onClose={() => setOpenCompensationDialog(false)}
        onSuccess={handleCompensationSuccess}
        companyId={company?.id}
      />

      <ApplyPackageDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        companyId={user.id}
        onSuccess={() => {
          fetchCompanyDetails();
        }}
      />

      <AdjustSubscriptionDialog
        open={openAdjustDialog}
        onClose={() => setOpenAdjustDialog(false)}
        companyId={company?.id}
        onSuccess={() => {
          fetchCompanyDetails();
        }}
      />
    </div>
  );
};

export default UserPackage;